import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInputWithIcon } from "../../components/DropdownInputWithIcon/DropdownInputWithIcon";
import { CheckboxInput } from "../../components/CheckboxInput/CheckboxInput";
import { RoleType } from "../../models/Role";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { activeUser } from "../../services/AuthService";
import { createProduct, updateOneProduct } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import "./NewProductForm.scss";

export interface NewProductFormProps {
  show?: string;
  product?: Product;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

export const NewProductForm = (p: NewProductFormProps) => {
  const {
    product,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonResetStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [visibleNewProductForm, setVisibleNewProductForm] =
    useState<boolean>(false);
  const [allCategories, setAllCategories] = useState<ProductCategory[]>([]);
  const [filteredProductCategories, setFilteredProductCategories] = useState<
    ProductCategory[]
  >([]);
  const [newProduct, setNewProduct] = useState<Product>({
    id: product?.id ?? "", //@ts-ignore
    type: product?.type ?? null,
    code: product?.code ?? "",
    name: product?.name ?? "",
    measuringUnit: product?.measuringUnit ?? "",
    isWeighbridge: product?.isWeighbridge ?? false,
    isNatural: product?.isNatural ?? false,
  });

  const canEdit =
    activeUser.role?.name === RoleType.Superadmin ||
    activeUser.role?.name === RoleType.Administrator ||
    activeUser.role?.name === RoleType.Backoffice;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshCategories = async () => {
    const resp = await getAllProductCategories();
    const list: ProductCategory[] = resp?.data?.result ?? resp?.data ?? [];
    setAllCategories(list);
  };

  useEffect(() => {
    refreshCategories().catch((e: any) => {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri učitavanju kategorija")),
        life: 3500,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (show) {
      setVisibleNewProductForm(true);
    }
  }, [show]);

  useEffect(() => {
    if (product) {
      setNewProduct(product);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    // filtracija po tipu robe
    const type = newProduct?.type;
    setFilteredProductCategories(allCategories.filter((x) => x.type === type));
  }, [allCategories, newProduct?.type]);

  const showModal = () => {
    setVisibleNewProductForm(!visibleNewProductForm ? true : false);
  };

  const handleNewProductForm = (prop: string, value: any) => {
    setNewProduct((newProduct) => ({
      ...newProduct,
      [prop]: value,
    }));
    // ako promeniš tip, očisti kategoriju (da ne ostane pogrešna)
    if (prop === "type") {
      setNewProduct((prev) => ({
        ...prev,
        type: value,
        category: undefined,
      }));
    }
  };

  const handleProductCategoryChange = (_prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.category = allCategories.find((item) => item.id == value);
    setNewProduct(copyOfNewProduct);
  };

  const handleResetOnly = () => {
    setNewProduct({
      id: "",
      // @ts-ignore
      type: null,
      code: "",
      name: "",
      measuringUnit: "",
      category: undefined,
      isWeighbridge: false,
      isNatural: false,
    });
    reset();
  };

  const handleCloseAndReset = () => {
    handleResetOnly();
    setVisibleNewProductForm(false);
  };

  const onSubmit = async () => {
    try {
      // payload normalizacija
      const payload: Partial<Product> = {
        code: newProduct.code,
        name: newProduct.name,
        type: newProduct.type,
        measuringUnit: newProduct.measuringUnit,
        isWeighbridge: newProduct.isWeighbridge,
        isNatural: newProduct.isNatural,
        category: newProduct.category?.id
          ? ({ id: newProduct.category.id } as any)
          : undefined,
      };

      if (newProduct.id) {
        const resp = await updateOneProduct(newProduct.id, payload);
        ensureSuccess(resp);
        toast.current?.show({
          severity: "success",
          detail: String(t("Uspešno izmenjeni podaci")),
          life: 3000,
        });
      } else {
        const resp = await createProduct(payload);
        ensureSuccess(resp);
        toast.current?.show({
          severity: "success",
          detail: String(t("Uspešno dodati novi podaci")),
          life: 3000,
        });
      }

      refreshingData?.();
      handleCloseAndReset();
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri čuvanju proizvoda")),
        life: 3500,
      });
    }
  };

  const onError = () => {
    toast.current?.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-boxes" style={{ fontSize: 18 }}></i>
      </span>
      {newProduct.id === "" ? (
        <span>{t("Dodavanje nove robe")}</span>
      ) : (
        <span>{t("Ažuriranje robe")}</span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Potvrdi")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={!canEdit}
      />
      <ButtonInput
        labelText={t("Resetuj")}
        onButtonClick={handleResetOnly}
        addStyle={addButtonResetStyle}
        disabled={!canEdit}
      />
    </div>
  );

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={() => {
          handleResetOnly();
          setVisibleNewProductForm(true);
        }}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visibleNewProductForm ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            className="new-product-modal"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={showModal}
          >
            <div className="new-product-form-body">
              <TextInputWithIcon
                id="code"
                value={newProduct.code}
                onValueChange={handleNewProductForm}
                labelText={`${t("Šifra robe")} *`}
                icon="bi bi-upc-scan"
                iconAlign="left"
                placeholderText={t("Kreirajte šifru robe")}
                control={control}
                errors={errors}
                setValue={setValue}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="name"
                value={newProduct.name}
                onValueChange={handleNewProductForm}
                labelText={`${t("Naziv robe")} *`}
                icon="bi bi-boxes"
                iconAlign="left"
                placeholderText={t("Unesite naziv robe")}
                control={control}
                errors={errors}
                setValue={setValue}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <DropdownInputWithIcon
                id="type"
                value={newProduct.type}
                onValueChange={handleNewProductForm}
                options={[
                  {
                    label: t("Merkantila"),
                    value: ProductType.MercantileGoods,
                  },
                  {
                    label: t("Repromaterijal"),
                    value: ProductType.RawMaterial,
                  },
                ]}
                icon="bi bi-boxes"
                filter={false}
                labelText={`${t("Dodeli tip robe")} *`}
                placeholderText={t("Izaberite tip robe")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <DropdownInputWithIcon
                id="category"
                value={newProduct?.category?.id ?? ""}
                onValueChange={handleProductCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={filteredProductCategories}
                icon="bi bi-boxes"
                labelText={`${t("Vrsta robe")} *`}
                placeholderText={t("Izaberite vrstu robe")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <DropdownInputWithIcon
                id="measuringUnit"
                value={newProduct.measuringUnit as string}
                onValueChange={handleNewProductForm}
                options={[
                  { label: "Kom", value: "Kom" },
                  { label: "KG", value: "KG" },
                  { label: "TO", value: "TO" },
                  { label: "L", value: "L" },
                  { label: "PG", value: "PG" },
                  { label: "MVK", value: "MVK" },
                ]}
                icon="bi bi-minecart"
                filter={false}
                labelText={`${t("Merna jedinica")} *`}
                placeholderText={t("Izaberite mernu jedinicu robe")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              {newProduct?.type === ProductType.RawMaterial && (
                <CheckboxInput
                  id="isWeighbridge"
                  checked={newProduct.isWeighbridge as boolean}
                  onValueChange={handleNewProductForm}
                  labelText={t("Korišćenje vage")}
                  control={control}
                  errors={errors}
                />
              )}
              {newProduct?.type === ProductType.MercantileGoods && (
                <CheckboxInput
                  id="isNatural"
                  checked={newProduct.isNatural as boolean}
                  onValueChange={handleNewProductForm}
                  labelText={t("Naturalna roba")}
                  control={control}
                  errors={errors}
                />
              )}
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
